import axios from 'axios'
import appConfig from '@/configs/app.config'
import endpointConfig from '@/configs/endpoint.config'
import { tokenStorage } from '@/store/authStore'

/**
 * The ONE token-refresh path, shared by the axios interceptor and the chat
 * SSE stream (which uses fetch and never passes through axios).
 *
 * Sharing is not an optimisation — it is correctness. The backend rotates
 * refresh tokens and blacklists the presented one, so two independent
 * refreshes racing each other means the loser presents a dead token and the
 * user is signed out mid-session (frontend CLAUDE.md §6). Every caller,
 * whatever transport it uses, must wait on the same in-flight promise.
 */

/**
 * Bare client for the refresh call: it must not pass through the app's
 * interceptors, or a failing refresh would recurse into itself.
 */
const refreshClient = axios.create({ baseURL: appConfig.apiPrefix })

let refreshPromise: Promise<string> | null = null

const requestNewAccessToken = async (): Promise<string> => {
    const refreshToken = tokenStorage.refresh

    if (!refreshToken) {
        throw new Error('No refresh token stored')
    }

    const { data } = await refreshClient.post<{
        token: string
        refreshToken: string
    }>(endpointConfig.refresh, { refreshToken })

    // Both halves must be stored. The backend rotates refresh tokens and
    // blacklists the presented one, so keeping the old refresh token would
    // sign the user out at the *next* refresh rather than this one — a bug
    // that only shows up one token lifetime later.
    tokenStorage.set(data.token, data.refreshToken)

    return data.token
}

const refreshAccessToken = () => {
    // Collapse concurrent refreshes onto one in-flight request.
    if (!refreshPromise) {
        refreshPromise = requestNewAccessToken().finally(() => {
            refreshPromise = null
        })
    }
    return refreshPromise
}

export default refreshAccessToken
